// src/components/HamburgerActions.jsx
import React, { useState, useRef, useEffect } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Menu, X, BookOpen, Info, Plus, Building2 } from "lucide-react"; // Or your icon library
import { useTheme } from "../utils/theme";
import AutocompleteInput from "./AutocompleteInput";
import { LoginForm } from "../App.js"; // Assuming these are exported from App.js
import { SavePatternModal } from "../components/SavePatternModal";
import { LoadPatternModal } from "./LoadPatternModal";
import { SignUpForm } from "../components/SignUpForm";
import { SOUND_OPTIONS } from "../data/musicData.js";
import { createSharedPattern } from "../utils/sharingDb";
import { warmUpSpeech } from "../utils/speech";
import SaveAsModal from "./SaveAsModal";
import PatternManagementModal from "./PatternManagementModal";

function HamburgerActions({
  patternName,
  deleteFolder,
  selectedFolderForDeletion,
  setSelectedFolderForDeletion,
  selectedFolder,
  setSelectedFolder,
  setPatternName,
  newFolderName,
  setNewFolderName,
  folderName,
  setFolderName,
  savedPatterns,
  onSavePattern,
  onSaveAs,
  onManualSave,
  onRenamePattern,
  onLoadPattern,
  onDeletePattern,
  onImportMIDI,
  onExportMIDI,
  onImportMetronomeData,
  onImportMusicXML,
  onOpenSheetMusicImport,
  onExportMetronomeData,
  onExportMp3,
  aTuning,
  setATuning,
  stressOn,
  setStressOn,
  subscriptionTier,
  handleUpgrade,
  currentUser,
  setCurrentUser,
  handleGoogleSignIn,
  handleSignOut,
  setShowTutorial,
  setExportToLibraryPattern,
  measures,
  tempo,
  useTempoChanges,
  enableAdditionalTempoChanges,
  tempoChanges,
  muteRandomBeats,
  mutePercentage,
  loopMode,
  startMeasureNumber,
  selectedNoteValue,
  setActiveTab,
  timeSignatureColors,
  setTimeSignatureColors,
  showTimeSignatureColorsModal,
  setShowTimeSignatureColorsModal,
  rehearsalLetters,
  modulateOnLoop,
  modLoopInterval,
  modLoopDirection,
  autoSaveEnabled,
  setAutoSaveEnabled,
  isAutoSaving,
  lastAutoSave,
  triggerAutosave,
  isPlaying,
  showSaveModal,
  setShowSaveModal,
  isIOS,
  isInPWA,
  setShowPWAInstructions,
  enablePercentageScaling,
  setEnablePercentageScaling,
  announceRehearsalMarks,
  setAnnounceRehearsalMarks,
  rehearsalCountInMeasures,
  setRehearsalCountInMeasures,
  setMeasures,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, setTheme, themes } = useTheme(currentUser);
  const activeTheme = themes.find((t) => t.id === theme) || themes[0];
  const [loadSectionOpen, setLoadSectionOpen] = useState(false);
  const [importSectionOpen, setImportSectionOpen] = useState(false);
  const [exportSectionOpen, setExportSectionOpen] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showSignUpForm, setShowSignUpForm] = useState(false);
  const [showPatternManagementModal, setShowPatternManagementModal] = useState(false);
  // ADD THESE BACK - they're still needed for existing sections:
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [advancedSectionOpen, setAdvancedSectionOpen] = useState(false);
  const menuRef = useRef(null);
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);

  // Effect for handling clicks outside the menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target) && menuOpen) {
        setMenuOpen(false);
      }
    }

    // Add event listener when menu is open
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    // Cleanup the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const uniqueFolders = [
    ...new Set(
      savedPatterns
        .filter((pattern) => pattern.folder)
        .map((pattern) => pattern.folder)
    ),
  ];

  const handleBulkBeatSoundChange = (newSoundId) => {
    if (!measures || !setMeasures) return;
    
    const selectedSound = SOUND_OPTIONS.find(sound => sound.id === newSoundId);
    if (!selectedSound) return;

    setMeasures(prevMeasures => 
      prevMeasures.map(measure => ({
        ...measure,
        beatSounds: measure.beatSounds.map(beat => ({
          ...beat,
          id: selectedSound.id,
          name: selectedSound.name,
          type: selectedSound.type,
          color: selectedSound.color,
          frequency: selectedSound.frequency,
          accentFrequency: selectedSound.accentFrequency,
        }))
      }))
    );
  };

  return (
    <div className="relative inline-block" ref={menuRef}>
      {/* Hamburger Button */}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="p-2 bg-gray-200 border rounded hover:bg-gray-300"
        aria-label={menuOpen ? "Close menu" : "Open menu"}
      >
        {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {menuOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white border border-gray-300 rounded shadow-lg z-[100] text-sm">
          {/* User Section (only shows tier info) */}
          {currentUser && (
            <div className="px-4 py-2 border-t">
              <div className="flex flex-col items-start space-y-2">
                <p className="text-xs text-gray-700">
                  Logged in as:{" "}
                  <span className="font-semibold">{currentUser.email}</span>
                </p>

                <div className="mt-4">
                  <p>
                    Your current tier: <strong>{subscriptionTier}</strong>
                  </p>

                  {subscriptionTier === "free" && (
                    <div className="space-y-2 mt-2">
                      <button onClick={() => handleUpgrade(currentUser)}>
                        Upgrade
                      </button>
                    </div>
                  )}

                  {subscriptionTier === "basic" && (
                    <div className="space-y-2 mt-2">
                      <p>
                        You're on the Basic plan. Enjoy up to 10 cloud patterns!
                      </p>
                      <button
                        onClick={() => handleUpgrade(currentUser)}
                        className="px-3 py-1 bg-green-600 text-white rounded"
                      >
                        Upgrade to Pro
                      </button>
                    </div>
                  )}

                  {subscriptionTier === "pro" && (
                    <p>You're on the Pro plan. Enjoy all premium features!</p>
                  )}
                </div>

                <button
                  onClick={() => {
                    setActiveTab("orgAdmin");
                    setMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-between px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded mt-2"
                >
                  <span className="font-medium">Organization</span>
                  <Building2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {/* SAVE PROJECT SECTION */}
          <div className="px-4 py-2 border-b">
          <button
  onClick={() => {
    setShowSaveModal(true); // Show the modal first
    setMenuOpen(false);
  }}
  disabled={isAutoSaving || !currentUser}
  className="w-full flex items-center justify-between px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded mb-2"
>
  {isAutoSaving ? 'Saving...' : 'Save (Ctrl+S / ⌘+S)'}
</button>

  <button
  onClick={() => {
    console.log("Save As button clicked!"); // ADD THIS DEBUG LINE
    setShowSaveAsModal(true);
  }}
  // disabled={isPlaying || !currentUser}
className="w-full flex items-center justify-between px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
  
>
  Save As...
</button>

{/*
           <button
               onClick={() => setShowSaveModal(true)}
              className="block w-full text-left px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded"
            >
              Save Project (Ctrl+S / ⌘+S)
          </button>
          */}
          </div>


          {/* PATTERN MANAGEMENT SECTION - Replace the old rename and delete sections */}
          {savedPatterns && savedPatterns.length > 0 && (
            <div className="px-4 py-2 border-t">
              <button
                onClick={() => {
                  setShowPatternManagementModal(true);
                  setMenuOpen(false);
                }}
                className="w-full flex items-center justify-between px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
              >
                <span className="font-medium">Manage Patterns</span>
              </button>
            </div>
          )}

          {/* LOAD SECTION */}
          {savedPatterns && savedPatterns.length > 0 && (
            <div className="px-4 py-2 border-t">
              <button
                onClick={() => setShowLoadModal(true)}
                className="w-full flex items-center justify-between px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
              >
                <span className="font-medium">Load Project</span>
              </button>
            </div>
          )}

          {/* IMPORT SECTION */}
          <div className="px-4 py-2 border-t">
            <button
              onClick={() => setImportSectionOpen(!importSectionOpen)}
              className="w-full flex items-center justify-between px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
            >
              <span className="font-medium">Import Options</span>
              {importSectionOpen ? "▲" : "▼"}
            </button>

            {importSectionOpen && (
              <div className="mt-2">
                {/* Import Metronome Data (JSON) */}
                <label
                  htmlFor="importMetronomeData"
                  className="block px-4 py-2 hover:bg-gray-100 cursor-pointer border-t text-sm"
                >
                  Import FlowFrame (.FF)
                </label>
                <input
                  id="importMetronomeData"
                  type="file"
                  accept=".ff,.json"
                  onChange={(e) => {
                    onImportMetronomeData(e);
                    setMenuOpen(false);
                  }}
                  className="hidden"
                />

                {/* Import MusicXML — generates a FlowFrame timeline from
                    sheet-music notation (Audiveris OMR output or any
                    MusicXML export). Pro-only: it produces a FlowFrame
                    pattern, the same value-tier as the OMR import. */}
                {onImportMusicXML && subscriptionTier === "pro" && (
                  <>
                    <label
                      htmlFor="importMusicXML"
                      className="block px-4 py-2 hover:bg-gray-100 cursor-pointer border-t text-sm"
                    >
                      Import MusicXML (.xml / .musicxml)
                    </label>
                    <input
                      id="importMusicXML"
                      type="file"
                      accept=".xml,.musicxml"
                      onChange={(e) => {
                        onImportMusicXML(e);
                        setMenuOpen(false);
                      }}
                      className="hidden"
                    />
                  </>
                )}

                {/* Import Sheet Music PDF — Pro-only. Runs an OMR pipeline
                    on a PDF (from the user's score library or a fresh
                    upload) and turns it into a FlowFrame timeline. */}
                {onOpenSheetMusicImport && subscriptionTier === "pro" && (
                  <button
                    type="button"
                    onClick={() => {
                      onOpenSheetMusicImport();
                      setMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 border-t text-sm"
                  >
                    Import Sheet Music PDF (OMR)
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium align-middle">
                      Beta
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
          {/* EXPORT SECTION */}
          <div className="px-4 py-2">
            <button
              onClick={() => setExportSectionOpen(!exportSectionOpen)}
              className="w-full flex items-center justify-between px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
            >
              <span className="font-medium">Export Options</span>
              {exportSectionOpen ? "▲" : "▼"}
            </button>

            {exportSectionOpen && (
              <div className="mt-2 space-y-2">
                {/* Add Copy Link button at the top */}
                <button
                  onClick={async (event) => {
                    try {
                      // Create pattern data object
                      const patternData = {
                        name: patternName || "Untitled Pattern",
                        folder: folderName || "Uncategorized",
                        tempo: tempo || 60,
                        useTempoChanges: useTempoChanges || false,
                        enableAdditionalTempoChanges: enableAdditionalTempoChanges || false,
                        tempoChanges: tempoChanges || [],
                        aTuning: aTuning || 440,
                        muteRandomBeats: muteRandomBeats || false,
                        mutePercentage: mutePercentage || 0,
                        loopMode: loopMode || "loop",
                        measures: measures || [],
                        startMeasureNumber: startMeasureNumber || 1,
                        stressOn: stressOn || false,
                        selectedNoteValue: selectedNoteValue || {
                          label: "Quarter Note",
                          factor: 1,
                        },
                        rehearsalLetters: rehearsalLetters || {},
                        timeSignatureColors: timeSignatureColors || {},
                        modulateOnLoop: modulateOnLoop || false,
                        modLoopInterval: modLoopInterval || 0,
                        modLoopDirection: modLoopDirection || "up",
                      };

                      console.log("Creating shared pattern with:", patternData);
                      // Save pattern to Firebase and get ID
                      const patternId = await createSharedPattern(patternData);
                      console.log("Created pattern with ID:", patternId);
                      
                      // Create a shorter URL with just the ID
                      const patternLink = `${window.location.origin}/?shared=${patternId}`;
                      
                      // Copy to clipboard
                      await navigator.clipboard.writeText(patternLink);
                      
                      // Show success message
                      const button = event.target;
                      const originalText = button.textContent;
                      button.textContent = "Copied!";
                      setTimeout(() => {
                        button.textContent = originalText;
                      }, 2000);
                    } catch (err) {
                      console.error('Failed to create shared link:', err);
                      alert('Failed to create shared link. Please try again.');
                    }
                  }}
                  className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded"
                >
                  Copy Link
                </button>

                {/* Existing export options */}
                {subscriptionTier === "pro" ? (
                  <button
                    onClick={onExportMIDI}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded"
                  >
                    Export as MIDI
                  </button>
                ) : (
                  <button
                    onClick={handleUpgrade}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-gray-600"
                  >
                    Export as MIDI (Pro)
                  </button>
                )}

                <button
                  onClick={onExportMetronomeData}
                  className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded"
                >
                  Export as FlowFrame (.FF)
                </button>

                {onExportMp3 && (
                  <button
                    onClick={() => {
                      onExportMp3();
                      setMenuOpen(false);
                    }}
                    disabled={isPlaying}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                  >
                    Export as MP3 (audio)
                  </button>
                )}

                {/* Share to Library */}
                {currentUser ? (
                  <button
                    onClick={() => {
                      const currentPattern = {
                        name: patternName,
                        folder: folderName,
                        tempo,
                        useTempoChanges,
                        enableAdditionalTempoChanges,
                        tempoChanges,
                        aTuning,
                        muteRandomBeats,
                        mutePercentage,
                        loopMode,
                        measures,
                        startMeasureNumber,
                        stressOn,
                        selectedNoteValue,
                      };
                      setExportToLibraryPattern(currentPattern);
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded"
                  >
                    Share to Library
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      alert("Please log in to share patterns to the library");
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-gray-600"
                  >
                    Share to Library (Login Required)
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="px-4 py-2 border-t">
            <button
              onClick={async () => {
                localStorage.removeItem("tutorialCompleted");

                // Update Firestore first if user is logged in
                if (currentUser) {
                  try {
                    const userDocRef = doc(db, "users", currentUser.uid);
                    await updateDoc(userDocRef, {
                      tutorialCompleted: false,
                    });
                  } catch (error) {
                    console.error("Error updating Firestore:", error);
                  }
                }

                // Only after Firestore is updated, update the UI state
                setTimeout(() => {
                  setShowTutorial(true);
                  setMenuOpen(false);
                }, 100);
              }}
              className="w-full flex items-center justify-between px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
            >
              <span className="font-medium">View Tutorial</span>
              <BookOpen className="w-4 h-4" />
            </button>
          </div>

           {/* ADVANCED SECTION */}
  <div className="px-4 py-2 border-t">
    <button
      onClick={() => setAdvancedSectionOpen(!advancedSectionOpen)}
      className="w-full flex items-center justify-between px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
    >
      <span className="font-medium">Advanced</span>
      {advancedSectionOpen ? "▲" : "▼"}
    </button>

    {advancedSectionOpen && (
      <div className="mt-2 space-y-2">
        {/* Autosave Toggle */}
        <div className="px-2 py-2 bg-gray-50 rounded">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoSaveEnabled}
              onChange={(e) => setAutoSaveEnabled(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium">Enable Autosave</span>
          </label>
          
          {/* Autosave Status Info */}
          {autoSaveEnabled && (
            <div className="mt-2 text-xs text-gray-600">
              {isAutoSaving ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-300 border-t-blue-600 mr-2"></div>
                  Saving...
                </div>
              ) : lastAutoSave ? (
                <div>
                  Last saved: {new Date(lastAutoSave).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              ) : (
                <div className="text-gray-500">
                  Auto-saves every 2 minutes when changes are made
                </div>
              )}
            </div>
          )}
          
          {!autoSaveEnabled && (
            <div className="mt-1 text-xs text-gray-500">
              Changes will only be saved manually
            </div>
          )}
        </div>

         {/* Percentage Scaling Toggle */}
         <div className="px-2 py-2 bg-gray-50 rounded">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enablePercentageScaling}
              onChange={(e) => setEnablePercentageScaling(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium">Enable Tempo Percentage Scaling</span>
          </label>

          <div className="mt-1 text-xs text-gray-500">
            Adds percentage controls to tempo slider
          </div>
        </div>

        {/* Speak Rehearsal Marks */}
        <div className="px-2 py-2 bg-gray-50 rounded">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!announceRehearsalMarks}
              onChange={(e) => {
                // Warm the speech synth from inside the click handler — this
                // is the most reliable user-gesture context, and getting the
                // engine spinning now means the first real announcement at
                // playback time isn't waiting on cold-start initialization.
                if (e.target.checked) warmUpSpeech();
                setAnnounceRehearsalMarks(e.target.checked);
              }}
              className="mr-2"
            />
            <span className="text-sm font-medium">Speak Rehearsal Marks</span>
          </label>

          <div className="mt-1 text-xs text-gray-500">
            Announces rehearsal letters/numbers on the downbeat during playback.
          </div>

          {announceRehearsalMarks && (
            <div className="mt-2">
              <label className="block text-xs font-medium mb-1">
                Count-in before each mark
              </label>
              <select
                value={rehearsalCountInMeasures ?? 0}
                onChange={(e) =>
                  setRehearsalCountInMeasures(parseInt(e.target.value, 10))
                }
                className="w-full p-1 border border-gray-300 rounded text-xs"
              >
                <option value={0}>Off</option>
                <option value={1}>1 measure</option>
                <option value={2}>2 measures</option>
                <option value={3}>3 measures</option>
                <option value={4}>4 measures</option>
              </select>
            </div>
          )}
        </div>

         {/* STRESS & TUNING */}
         <div className="px-4 py-2 border-t">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={stressOn}
                onChange={(e) => setStressOn(e.target.checked)}
              />
              <span className="ml-1 text-sm">Stress Patterns</span>
            </label>
          </div>

         {/* Bulk Beat Sound Change */}
         <div className="px-2 py-2 bg-gray-50 rounded">
                  <label className="block text-sm font-medium mb-2">
                    Change All Beat Sounds
                  </label>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkBeatSoundChange(e.target.value);
                      }
                    }}
                    className="w-full p-1 border border-gray-300 rounded text-xs"
                    defaultValue=""
                    disabled={isPlaying}
                  >
                    <option value="">Select sound to apply to all beats</option>
                    {SOUND_OPTIONS.map(sound => (
                      <option key={sound.id} value={sound.id}>
                        {sound.name}
                      </option>
                    ))}
                  </select>
                  <div className="mt-1 text-xs text-gray-500">
                    Changes all main beats to the selected sound (excludes subdivisions and tuplets)
                  </div>
                </div>

        {/* Time Signature Colors */}
        <button
          onClick={() => {
            setShowTimeSignatureColorsModal(true);
            setMenuOpen(false);
          }}
          className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded"
        >
          Time Signature Colors
        </button>
      </div>
    )}
  </div>
         
          <div className="px-4 py-2 border-t">
  <label className="font-medium block mb-1">Tuning (A=? Hz)</label>
  <input
    type="number"
    min="200"
    max="800"
    step="0.1"
    value={aTuning}
    onChange={(e) => {
      const value = parseFloat(e.target.value);
      if (!isNaN(value) && value > 0) {
        setATuning(value);
      }
    }}
    onBlur={() => setMenuOpen(false)}
    className="border p-1 rounded w-full"
    placeholder="440"
  />
</div>
          
          {/* COLOR SCHEME */}
          <div className="px-4 py-2 border-t">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Color scheme
            </p>
            <div className="flex items-center gap-2" role="radiogroup" aria-label="Color scheme">
              {themes.map((t) => {
                const selected = t.id === theme;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={t.name}
                    title={`${t.name} — ${t.description}`}
                    onClick={() => setTheme(t.id)}
                    className={`h-7 w-7 rounded-full ring-2 ring-offset-2 transition-transform ${
                      selected ? "ring-gray-800 scale-110" : "ring-transparent hover:ring-gray-300 hover:scale-105"
                    }`}
                    style={{ background: `linear-gradient(135deg, ${t.primary} 50%, ${t.accent} 50%)` }}
                  />
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {activeTheme.name} · {currentUser ? "synced to your account" : "saved on this device"}
            </p>
          </div>

          {/* Sign Out Button - Only show if user is logged in */}
          {currentUser && (
            <div className="px-4 py-2 border-t mt-auto">
              <button
                onClick={() => {
                  handleSignOut(setShowLoginForm);
                  setMenuOpen(false);
                }}
                className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
              >
                Sign Out
              </button>
            </div>
          )}

          {/* Install App Button */}
          {isIOS && !isInPWA && (
            <button
              onClick={() => setShowPWAInstructions(true)}
              className="w-full text-left px-3 py-1 text-sm hover:bg-gray-100 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Install App
            </button>
          )}
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <SavePatternModal
          onClose={() => setShowSaveModal(false)}
          patternName={patternName}
          setPatternName={setPatternName}
          folderName={folderName}
          setFolderName={setFolderName}
          onSavePattern={async (options = {}) => {
            // Close the modal first
            setShowSaveModal(false);
            setMenuOpen(false);
            
            // Then call the save function with any provided options
            await onSavePattern(options);
          }}
          savedPatterns={savedPatterns}
        />
      )}

      {/* Save As Modal */}
{showSaveAsModal && (
  <SaveAsModal
    isOpen={showSaveAsModal}
    onClose={() => setShowSaveAsModal(false)}
    currentPatternName={patternName}
    currentFolderName={folderName}
    savedPatterns={savedPatterns}
    subscriptionTier={subscriptionTier}
    onSave={(newName, newFolder) => {
      onSaveAs(newName, newFolder);
      setShowSaveAsModal(false);
    }}
  />
)}

      {showLoadModal && (
        <LoadPatternModal
          onClose={() => setShowLoadModal(false)}
          savedPatterns={savedPatterns}
          onLoadPattern={onLoadPattern}
          onDeletePattern={onDeletePattern}
        />
      )}

      {/* Pattern Management Modal */}
      {showPatternManagementModal && (
        <PatternManagementModal
          onClose={() => setShowPatternManagementModal(false)}
          savedPatterns={savedPatterns}
          onRenamePattern={onRenamePattern}
          onDeletePattern={onDeletePattern}
          deleteFolder={deleteFolder}
        />
      )}
    </div>
  );
}

export default HamburgerActions;
